import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import {
  PROMO_CHIP_WORDS,
  PROMO_STATUS_LABELS,
  PROMO_STATUS_TONE,
  PromoStatus,
} from "shared/utils";
import { localTzDate } from "shared/utils/dayjs";

// The status type lives with the label + tone maps in shared/utils;
// re-exported so the Paket / Membership pages keep importing it from here.
export type { PromoStatus } from "shared/utils";

// Priority order for sorting/grouping promo lists: live promos first, then
// not-yet-started ones, then archived ones last.
export const PROMO_STATUS_ORDER: Record<PromoStatus, number> = {
  active: 0,
  upcoming: 1,
  ended: 2,
};

// Minimal shape needed to derive a promo's status — satisfied by
// PackageProduct. `promo_active`/`promo_ended` come straight from the
// server (isPromoActive/isPromoEnded in util/product.ts), so this never
// re-derives the date math client-side; it only picks the label/tone.
export type PromoStatusInput = {
  is_promo: boolean;
  promo_active: boolean;
  promo_ended: boolean;
  promo_starts_at: string | null;
  promo_ends_at: string | null;
};

// promo_active is false both BEFORE promo_starts_at and AFTER promo_ends_at,
// so "not active" alone can't distinguish "not started yet" from "over" —
// that's the source of the Berakhir/Mendatang mislabeling bug this fixes.
// promo_ended (purely end-date-based) is the one field that isolates "over";
// anything is_promo that's neither active nor ended must be upcoming.
export const promoStatusOf = (product: PromoStatusInput): PromoStatus | null => {
  if (!product.is_promo) return null;
  if (product.promo_active) return "active";
  if (product.promo_ended) return "ended";
  return "upcoming";
};

// Derived from the dates for analytics rows; it lives in shared/utils with
// the label and tone maps because the report PDFs read it too.
export { promoStatusOfDates } from "shared/utils";

// Bare status word (filters, sort-group labels): fixed English like every
// status badge. No "Promo" prefix, no date.
export const promoStatusWord = (status: PromoStatus): string => PROMO_STATUS_LABELS[status];

// Short date in the "en" locale so the chip reads the same in both
// languages ("23 Aug", never "23 Agt").
const chipDate = (value: string) => localTzDate(value, "en").format("DD MMM");

// Full chip label, e.g. "Promo · Until 23 Aug" / "Promo · Starts 20 Aug" /
// "Promo · Ended 19 Jul": the same "verb + date" shape across all three
// statuses, so Ended isn't the odd one out next to the other two, and the
// date visibly backs up the most-recently-ended-first sort order.
const labelFor = (status: PromoStatus, product: PromoStatusInput): string => {
  const { chip, until, starts, ended } = PROMO_CHIP_WORDS;
  if (status === "active") {
    return product.promo_ends_at ? `${chip} · ${until} ${chipDate(product.promo_ends_at)}` : chip;
  }
  if (status === "upcoming") {
    return product.promo_starts_at ? `${chip} · ${starts} ${chipDate(product.promo_starts_at)}` : chip;
  }
  return product.promo_ends_at ? `${chip} · ${ended} ${chipDate(product.promo_ends_at)}` : `${chip} · ${ended}`;
};

// Within a status group, the most URGENT/RECENT item sorts first: an Aktif
// promo ending soonest needs attention before one that still has weeks left;
// a Mendatang promo starting soonest is the one worth prepping for; Berakhir
// promos sort most-recently-ended first so ancient dead promos sink to the
// bottom instead of leading the group. Missing dates sort last within their
// group (Infinity) rather than crashing the comparison.
const promoSortKey = (status: PromoStatus, product: PromoStatusInput): number => {
  if (status === "active") {
    return product.promo_ends_at ? new Date(product.promo_ends_at).getTime() : Infinity;
  }
  if (status === "upcoming") {
    return product.promo_starts_at ? new Date(product.promo_starts_at).getTime() : Infinity;
  }
  return product.promo_ends_at ? -new Date(product.promo_ends_at).getTime() : Infinity;
};

// Single comparator for every promo list/column: Aktif → Mendatang →
// Berakhir, then the urgency ordering above within each group, then
// alphabetical as a final tiebreak. Shared so the Paket table's default
// order, its Status Promo column sort, and the Membership grid's default
// order can never drift apart.
export const comparePromoStatus = <T extends PromoStatusInput & { name?: string | null }>(
  a: T,
  b: T,
): number => {
  const statusA = promoStatusOf(a) ?? "ended";
  const statusB = promoStatusOf(b) ?? "ended";
  const rankA = PROMO_STATUS_ORDER[statusA];
  const rankB = PROMO_STATUS_ORDER[statusB];
  if (rankA !== rankB) return rankA - rankB;
  const keyA = promoSortKey(statusA, a);
  const keyB = promoSortKey(statusB, b);
  if (keyA !== keyB) return keyA - keyB;
  return (a.name ?? "").localeCompare(b.name ?? "");
};

// Single source for the promo status chip — shared by the Paket table, the
// Membership card grid, and the class-detail package/membership tables, so
// the tone/label/status derivation lives in exactly one place. Mirrors
// ScheduleStatusBadge's pattern (see that file for the precedent).
export const PromoStatusBadge = ({ product }: { product: PromoStatusInput }) => {
  const status = promoStatusOf(product);
  if (!status) return null;
  return <StatusBadge tone={PROMO_STATUS_TONE[status]} label={labelFor(status, product)} />;
};
